import React, { useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { useQuery as useGraphQLQuery } from "@apollo/client/react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { INTERNAL_WORK_ORDERS_QUERY } from "../../../constants/graphql/queries";
import InternalTableSkeleton from "./InternalTableSkeleton";
import InternalTable from "./InternalTable";
import InternalWorkOrderProjectModal from "./InternalWorkOrderProjectModal";
import InternalWorkOrderEditModal from "../../../views/inventory/requests/InternalWorkOrderEditModal";
import BillingTotalsModal, {
  BILLING_TOTALS_PARAMS,
} from "../billing/BillingTotalsModal";
import BillingLineItemsExportModal, {
  BILLING_LINE_ITEMS_EXPORT_PARAMS,
} from "../billing/BillingLineItemsExportModal";
import { useProject } from "../ProjectProvider";
import useQuery from "../../../hooks/useQuery";
import useRoles from "../../../hooks/user/useRoles";
import useProjectMovewareLink from "src/hooks/useProjectMovewareLink";

const limit = 50;
const billingTotalsParams = Object.values(BILLING_TOTALS_PARAMS);
const billingLineItemsExportParams = Object.values(BILLING_LINE_ITEMS_EXPORT_PARAMS);

export default function Internal({ title }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const { project } = useProject();
  const showMovewareDetails = useProjectMovewareLink(projectId);
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [createInternalWorkOrderOpen, setCreateInternalWorkOrderOpen] = useState(false);
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] = useState(null);
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const canViewBilling = isAdminOrManager && project?.billing;
  const internalWorkOrderId = query.get("internalWorkOrderId");
  const queryParams = new URLSearchParams(location.search);

  const { loading, data: { internalWorkOrders = {} } = {}, refetch } = useGraphQLQuery(
    INTERNAL_WORK_ORDERS_QUERY,
    {
      variables: {
        page,
        limit,
        search,
        projectId: parseInt(projectId),
      },
    }
  );

  const list = internalWorkOrders?.collection || [];
  const { currentPage, totalPages, totalCount } = internalWorkOrders?.metadata || {};

  const handlePageChange = useCallback((_, value) => {
    setPage(value);
    const dialogContent = document.querySelector(".MuiDialogContent-root");
    if (dialogContent) {
      dialogContent.scrollTo({ top: 0, left: 0 });
    }
  }, []);

  const handleSearch = useCallback(() => {
    setSearch(searchValue);
    setPage(1);
  }, [searchValue]);

  const handleKeyPress = useCallback(
    event => {
      if (event.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleSearchChange = useCallback(e => {
    setSearchValue(e.target.value);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("internalWorkOrderId");
    navigate({ search: searchParams.toString() });
  }, [navigate, location.search]);

  const handleOpenBillingTotals = () => {
    const params = new URLSearchParams(location.search);
    params.set(BILLING_TOTALS_PARAMS.open, "true");
    params.set(BILLING_TOTALS_PARAMS.step, "0");
    navigate({ search: params.toString() });
    setMoreOptionsAnchorEl(null);
  };

  const handleOpenExportModal = () => {
    const params = new URLSearchParams(location.search);
    params.set(BILLING_LINE_ITEMS_EXPORT_PARAMS.open, "true");
    params.set(BILLING_LINE_ITEMS_EXPORT_PARAMS.step, "0");
    navigate({ search: params.toString() });
    setMoreOptionsAnchorEl(null);
  };

  const handleCloseBillingTotals = () => {
    const params = new URLSearchParams(location.search);
    billingTotalsParams.forEach(param => params.delete(param));
    navigate({ search: params.toString() });
  };

  const handleCloseExportModal = () => {
    const params = new URLSearchParams(location.search);
    billingLineItemsExportParams.forEach(param => params.delete(param));
    navigate({ search: params.toString() });
  };

  const headerAction = (
    <>
      <Tooltip title="More Options">
        <Button
          size="small"
          color="inherit"
          aria-label="Internal work orders more options"
          onClick={event => setMoreOptionsAnchorEl(event.currentTarget)}
          endIcon={<MoreVertIcon fontSize="small" />}
        >
          More Options
        </Button>
      </Tooltip>
      <Menu
        anchorEl={moreOptionsAnchorEl}
        open={Boolean(moreOptionsAnchorEl)}
        onClose={() => setMoreOptionsAnchorEl(null)}
      >
        <MenuItem onClick={handleOpenBillingTotals}>
          Internal Order Billing Totals
        </MenuItem>
        <MenuItem onClick={handleOpenExportModal}>
          Itemized Billing Excel Export
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <Paper>
      <Box
        p={2}
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent={{ xs: "flex-start", sm: "space-between" }}
      >
        {title ? title : <Typography fontWeight={500}>Internal Work Orders</Typography>}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          width={{ xs: "100%", sm: "auto" }}
          justifyContent="flex-end"
        >
          <Tooltip title="Create Internal Work Order" arrow>
            <Button
              variant="contained"
              color="primary"
              sx={{ padding: "6px", minWidth: 0 }}
              onClick={() => setCreateInternalWorkOrderOpen(true)}
            >
              <AddCircleOutlineOutlinedIcon />
            </Button>
          </Tooltip>
          <TextField
            value={searchValue}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            size="small"
            placeholder="Search"
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleSearch}>
                  <SearchIcon color="action" />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>
      {loading ? (
        <Box px={2} pb={3}>
          <InternalTableSkeleton />
        </Box>
      ) : (
        <Box px={2} pb={3}>
          <InternalTable
            list={list}
            refetch={refetch}
            page={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            showBillingCharge={canViewBilling}
            headerAction={canViewBilling ? headerAction : null}
            showMovewareDetails={showMovewareDetails}
          />
        </Box>
      )}
      {createInternalWorkOrderOpen && (
        <InternalWorkOrderProjectModal
          onClose={() => setCreateInternalWorkOrderOpen(false)}
          onSuccess={() => {
            refetch();
          }}
        />
      )}
      {internalWorkOrderId && (
        <InternalWorkOrderEditModal
          id={internalWorkOrderId}
          onClose={handleCloseEditModal}
          onSaved={refetch}
        />
      )}
      {canViewBilling && queryParams.get(BILLING_TOTALS_PARAMS.open) === "true" && (
        <BillingTotalsModal
          projectId={projectId}
          onClose={handleCloseBillingTotals}
        />
      )}
      {canViewBilling && queryParams.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.open) === "true" && (
        <BillingLineItemsExportModal
          projectId={projectId}
          onClose={handleCloseExportModal}
        />
      )}
    </Paper>
  );
}
